import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';
import YouTube from 'react-youtube';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { useCarousel } from './hooks/useCarousel';
import { useOnCookieChange } from '../../hooks/useOnCookieChange';
import { useCookiesAllowVideo } from '../../hooks/useCookiesAllowVideo';
import { CarouselFields, Slide } from './types/Carouse.types';
import withErrorCatch from '../../helper/withErrorCatch';
import './Carousel.scss';

import BaseBtn from '../@shared/BaseBtn';
import CookiesVideoDisclaimer from '../@shared/CookiesVideoDisclaimer';
import { useToggle } from 'react-use';

const Carousel: FC<{ fields: CarouselFields }> = ({ fields: { cardList } }) => {
  const [slides, sliderSettings, isSliderReady] = useCarousel(cardList);
  const [isSlidingPaused, setIsSlidingPaused] = useToggle(false);
  const [windowWidth, setWindowWidth] = useState(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      setWindowWidth(window.innerWidth);
    }
  }, []);

  const consent = useOnCookieChange();
  const cookiesAllowVideo = useCookiesAllowVideo(consent);

  const [change, toggleChange] = useToggle(false);

  useEffect(() => {
    if (isSlidingPaused) setIsSlidingPaused();
  }, [change]);

  useEffect(() => {
    console.log(isSlidingPaused);
  }, [isSlidingPaused]);

  const WhiteBox: FC<{ slide: Slide }> = useMemo(() => {
    return function Component({ slide }) {
      return (
        <div className={`CarouselSlide__white_box`}>
          <span>{slide?.preTitle}</span>
          <RichText tag="h3" field={{ value: slide?.title }} />
          <RichText tag="p" field={{ value: slide?.fullDescription }} />
          <BaseBtn
            className="Slide__button"
            fields={{
              label: { value: slide?.linkLabel.value },
              link: {
                value: {
                  href: slide?.linkUrl,
                  target: slide?.openInNewWindow ? '_blank' : '_self',
                },
              },
            }}
          />
        </div>
      );
    };
  }, []);

  const CarouselVideoSlide: FC<{ slide: Slide; _change: boolean }> =
    useMemo(() => {
      return function Component({ slide, _change }) {
        const [isPlaying, toggleIsPlaying] = useToggle(false);

        const videoId = slide.videoUrl.split('v=').at(-1).split('&')[0];
        useEffect(() => {
          toggleIsPlaying(false);
        }, [_change]);

        const handlePlay = useCallback(() => {
          toggleIsPlaying(true);
          setIsSlidingPaused(true);
        }, []);
        const handlePause = useCallback(() => {
          toggleIsPlaying(false);
          setIsSlidingPaused(false);
        }, []);

        const YoutubeVideo: FC = useMemo(() => {
          return () => (
            <YouTube
              videoId={videoId}
              opts={{
                height: '100%',
                width: '100%',
                playerVars: {
                  controls: 1,
                  listType: 'playlist',
                  rel: 0,
                  modestbranding: 1,
                  mute: 1,
                },
              }}
              onPlay={handlePlay}
              onPause={handlePause}
            />
          );
        }, [_change]);

        return (
          <div className="CarouselSlide CarouselSlide__video">
            <YoutubeVideo />

            {/* <CookiesVideoDisclaimer allowVideo={cookiesAllowVideo} topZero /> */}

            {isSliderReady && !isPlaying && <WhiteBox slide={slide} />}
          </div>
        );
      };
    }, [isSliderReady]);

  const CarouselImageSlide: FC<{ slide: Slide }> = useMemo(() => {
    return function Component({ slide }) {
      return (
        <div
          className={'CarouselSlide'}
          style={{
            backgroundImage: `url(${windowWidth < 1024 ? (slide?.imageMobileUrl ? slide?.imageMobileUrl : slide?.imageUrl) : slide?.imageUrl})`,
          }}
          title={windowWidth < 1024 ? (slide?.imageMobileAlt ? slide?.imageMobileAlt : slide?.imageAlt): slide?.imageAlt}
          aria-label={windowWidth < 1024 ? (slide?.imageMobileAlt ? slide?.imageMobileAlt : slide?.imageAlt): slide?.imageAlt}        
          >
          {isSliderReady && <WhiteBox slide={slide} />}
        </div>
      );
    };
  }, [isSliderReady]);

  return (
    <div className="Carousel">
      <Slider
        lazyLoad="ondemand"
        {...sliderSettings}
        autoplay={!isSlidingPaused}
        autoplaySpeed={isSlidingPaused ? 0 : 6000}
        afterChange={toggleChange}
      >
        {slides?.map((slide, index) =>
          slide?.videoUrl && slide.videoUrl.length ? (
            <CarouselVideoSlide key={index} slide={slide} _change={change} />
          ) : (
            <CarouselImageSlide key={index} slide={slide} />
          )
        )}
      </Slider>
    </div>
  );
};

export default withErrorCatch(Carousel);
